// Desktop main shell — the primary logged-in experience.
//
// Layout ("Rooms sidebar + common strip"):
//   ┌ Header: avatar+name · mobile · location switcher · logout ┐
//   ├ COMMON DEVICES strip (always visible for the location)    ┤
//   ├ Rooms sidebar │ selected room's devices │ device panel    ┤
//
// Flow: pick a location → its Common Devices + Rooms load. Pick a room → its
// devices show in the main area. Pick a device (from a room OR the common strip)
// → its controls open in the inline right-side panel, while the common strip and
// rooms stay visible. Common devices reload whenever the location changes.
//
// Pure front-end over existing endpoints — no backend changes.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemText, ListItemIcon,
  CircularProgress, IconButton, Divider, Grid, Chip, Button,
  Checkbox, FormControlLabel, Tooltip,
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  getRooms, getCommonDevices, getDevicesByRoom, getDevicesByUser,
  updateRoomSortOrder, updateDeviceSortOrder,
} from '../../../core/api/api';
import { arrayMove } from '../../../core/utils/reorder';
import { saveDeviceOrder, applyDeviceOrder } from '../../../core/utils/orderCache';
import { sortByOrder, orderOf } from '../../../core/utils/sortOrder';
import { useApp } from '../../../core/state/AppContext';
import { useHideOffline, useOnlineOnly } from '../../../core/hooks/useHideOffline';
import DeviceControlCard from '../components/DeviceControlCard';
import RoomScenesManager from './scenes/RoomScenesManager';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import { deviceGridSpan, deviceCardWidth } from '../../../core/devices/deviceMeta';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);
const unoOf = (d) => d && (d.UniqueNumber ?? d.uniqueNumber);

// Rooms and devices carry a SortOrder from the backend; respect it everywhere.
// Shared with the mobile shell — the two used to hold separate copies of this
// rule, and only one of them held any copy at all. See core/utils/sortOrder.

export default function DesktopShell() {
  // Locations, selected location and the Common Devices toggle come from the
  // global header/context.
  const {
    userId, locations, locationId, locationsLoading, locationsError, retryLocations,
    showCommon, editOrder, canReorder, canUpdateFirmware,
  } = useApp();
  // Which scope's Update Code dialog is open (null = none). One piece of state
  // covers both the room button here and the location button in the header.
  const [updateScope, setUpdateScope] = useState(null);
  const reordering = editOrder && canReorder;
  const [dragRoomIdx, setDragRoomIdx] = useState(null);
  const [dragDevIdx, setDragDevIdx] = useState(null);

  const [commonDevices, setCommonDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loadingLocData, setLoadingLocData] = useState(false);

  const [roomId, setRoomId] = useState(null);
  const [roomDevices, setRoomDevices] = useState([]);
  const [loadingRoom, setLoadingRoom] = useState(false);

  // When the location changes: reload Common Devices + Rooms, clear selections.
  useEffect(() => {
    if (locationId == null) return undefined;
    let alive = true;
    setLoadingLocData(true);
    setRoomId(null);
    setRoomDevices([]);
    (async () => {
      try {
        const [common, rms] = await Promise.all([
          getCommonDevices(userId, locationId).catch(() => null),
          getRooms(userId, locationId).catch(() => null),
        ]);
        if (!alive) return;
        setCommonDevices(sortByOrder(asArray(common)));
        const rooms2 = sortByOrder(asArray(rms));
        setRooms(rooms2);
        // Auto-select the first room (by sort order) so the grid isn't empty.
        setRoomId(rooms2.length ? rooms2[0].RoomID : null);
      } finally {
        if (alive) setLoadingLocData(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, locationId]);

  // Load a room's devices.
  //
  // Passing the location is what makes the list complete: the room endpoint
  // drops whole device types and getDevicesByRoom merges them back from the
  // location list (core/devices/roomMerge.js).
  //
  // The user's full list is still pulled here for the two things neither of
  // those replies carries: `ID` (needed to persist a reorder) and DeviceIDs 3,
  // 19 and 21 (door lock, IR blaster, single switch with external button), which
  // the backend excludes from BOTH queries. It has no RoomID, so those are
  // matched by Room + Location NAME.
  const fetchRoomDevices = useCallback(async (rid) => {
    const [byRoom, allRes] = await Promise.all([
      getDevicesByRoom(userId, rid, locationId).catch(() => null),
      getDevicesByUser(userId).catch(() => null),
    ]);
    const allDevs = asArray(allRes);
    // The room query omits DevicesInstalled.ID; the all-devices list has it (as
    // `ID`). Enrich every room row so device reorder has its DeviceInstalledID.
    const idByUno = new Map(allDevs.map((d) => [String(unoOf(d)), d.ID]));
    const roomDevs = asArray(byRoom).map((d) => ({ ...d, ID: d.ID ?? idByUno.get(String(unoOf(d))) }));
    const seen = new Set(roomDevs.map((d) => String(unoOf(d))));
    const room = rooms.find((r) => String(r.RoomID) === String(rid));
    const rname = String(room?.RoomName || '').trim().toLowerCase();
    const loc = locations.find((l) => String(l.LocationID) === String(locationId));
    // getLocations returns "<user>-<LocationName>" (e.g. "Sanjay Ailani-Factory")
    // but the all-devices list has the raw name ("Factory"). Match on the suffix.
    const lname = String(loc?.LocationName || '').trim().toLowerCase();
    const locMatches = (dl) => {
      const d = String(dl || '').trim().toLowerCase();
      return !lname || lname === d || lname.endsWith(`-${d}`);
    };
    const extras = rname
      ? allDevs.filter((d) => !seen.has(String(unoOf(d)))
          && String(d.RoomName || '').trim().toLowerCase() === rname
          && locMatches(d.LocationName))
      : [];
    // ⚠️ `GetListofDevicesbyUserId` CARRIES NO SortOrder — probed, the column is
    // simply absent — so these rows read as 0 and sorted to the FRONT, ahead of
    // the device the customer put first. They are the types both room and
    // location queries exclude (3, 19, 21), so there is no order to recover for
    // them; they are appended, which is the honest answer, rather than being
    // silently promoted to the top.
    const tail = roomDevs.reduce((m, d) => Math.max(m, orderOf(d)), 0) + 1;
    const numbered = extras.map((d, i) => ({ ...d, SortOrder: tail + i }));
    // Backend SortOrder first, then apply the local reorder override (covers the
    // merged-in devices whose SortOrder the API can't return).
    return applyDeviceOrder(userId, rid, sortByOrder([...roomDevs, ...numbered]), unoOf);
  }, [userId, rooms, locations, locationId]);

  // When a room is selected: load its devices.
  useEffect(() => {
    if (roomId == null) { setRoomDevices([]); return undefined; }
    let alive = true;
    setLoadingRoom(true);
    fetchRoomDevices(roomId)
      .then((devs) => { if (alive) setRoomDevices(devs); })
      .finally(() => { if (alive) setLoadingRoom(false); });
    return () => { alive = false; };
  }, [roomId, fetchRoomDevices]);

  // Re-fetch the current room + common devices so the online dots stay live
  // without a page reload — one list call each.
  //
  // ⚠️ THE CADENCE IS THE FIRMWARE'S, NOT A GUESS. This used to say devices
  // refresh their timestamp "every ~30s" and polled at 30s to match; the switch
  // firmware actually posts every 180000 ms (see deviceStatus.ONLINE_WINDOW_MS
  // for the source), so that was six requests per heartbeat, five of them
  // fetching a value that could not have changed. Polling at one minute still
  // shows a state change well inside the ten-minute window.
  useEffect(() => {
    const id = setInterval(async () => {
      if (editOrder) return; // don't reshuffle mid-drag
      try {
        if (roomId != null) {
          const devs = await fetchRoomDevices(roomId);
          if (devs) setRoomDevices(devs);
        }
        if (locationId != null) {
          const common = await getCommonDevices(userId, locationId).catch(() => null);
          if (common) setCommonDevices(sortByOrder(asArray(common)));
        }
      } catch (e) { /* ignore transient poll errors */ }
    }, 60000);
    return () => clearInterval(id);
  }, [userId, roomId, locationId, fetchRoomDevices, editOrder]);

  // Persist a new room order (global — Rooms.SortOrder) then reflect it.
  const persistRoomOrder = async (list) => {
    setRooms(list.map((r, i) => ({ ...r, SortOrder: i })));
    await Promise.all(list.map((r, i) => updateRoomSortOrder(r.RoomID, i).catch(() => null)));
  };
  const onDropRoom = (i) => {
    if (dragRoomIdx != null && dragRoomIdx !== i) persistRoomOrder(arrayMove(rooms, dragRoomIdx, i));
    setDragRoomIdx(null);
  };
  const moveRoom = (i, dir) => {
    const j = i + dir;
    if (j >= 0 && j < rooms.length) persistRoomOrder(arrayMove(rooms, i, j));
  };

  // Persist a new device order: cache locally (reliable for every device type,
  // incl. merged-in ones) AND write backend SortOrder where we have the ID.
  const persistDeviceOrder = async (list) => {
    setRoomDevices(list.map((d, i) => ({ ...d, SortOrder: i })));
    saveDeviceOrder(userId, roomId, list.map((d) => unoOf(d)));
    await Promise.all(list.map((d, i) => (d.ID != null
      ? updateDeviceSortOrder(userId, d.ID, i).catch(() => null) : null)));
  };
  const onDropDev = (i) => {
    if (dragDevIdx != null && dragDevIdx !== i) persistDeviceOrder(arrayMove(roomDevices, dragDevIdx, i));
    setDragDevIdx(null);
  };
  const moveDev = (i, dir) => {
    const j = i + dir;
    if (j >= 0 && j < roomDevices.length) persistDeviceOrder(arrayMove(roomDevices, i, j));
  };

  const selectedRoom = rooms.find((r) => String(r.RoomID) === String(roomId));

  // "Hide offline devices" — the same localStorage flag the mobile shell uses.
  //
  // Suspended while reordering: drag-and-drop and the ↑/↓ buttons address
  // devices by their INDEX in this list, so persisting an order computed from a
  // filtered list would drop every hidden device out of the saved order.
  const [hideOffline, setHideOffline] = useHideOffline();
  const filterOn = hideOffline && !reordering;
  const { devices: shownCommon } = useOnlineOnly(commonDevices, filterOn);
  const { devices: shownRoomDevices, hiddenCount: hiddenInRoom } = useOnlineOnly(roomDevices, filterOn);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>
      {locationsLoading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : locations.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="text.secondary">
            {locationsError
              ? "Couldn't load your locations. Check your connection and try again."
              : 'No locations available for this account.'}
          </Typography>
          {locationsError && (
            <Button variant="contained" onClick={retryLocations}>Retry</Button>
          )}
        </Box>
      ) : (
        <>
          {/* Page toolbar: the offline filter on the left, the location-wide
              firmware push (owner / super admin only) on the right. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1 }}>
            <Tooltip title={reordering
              ? 'Paused while you edit the order, so hidden devices keep their place'
              : 'Show only devices that reported recently'}
            >
              <FormControlLabel
                sx={{ mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={hideOffline}
                    onChange={(e) => setHideOffline(e.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="body2" color="text.secondary">
                    Hide offline devices
                  </Typography>
                )}
              />
            </Tooltip>
            {filterOn && hiddenInRoom > 0 && (
              <Chip size="small" variant="outlined" label={`${hiddenInRoom} hidden`} />
            )}
            <Box sx={{ flex: 1 }} />
            {canUpdateFirmware && locationId != null && (
              <Button
                size="small"
                color="warning"
                startIcon={<SystemUpdateAltIcon fontSize="small" />}
                onClick={() => setUpdateScope({
                  type: 'location',
                  id: locationId,
                  name: locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName || 'Location',
                })}
              >
                Update Code (Location)
              </Button>
            )}
          </Box>

          {/* Common Devices strip — shown only when the header toggle is green. */}
          {showCommon && (
            <>
              <CommonStrip devices={shownCommon} loading={loadingLocData} />
              <Divider />
            </>
          )}

          {/* Rooms | room devices | device controls */}
          <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* Rooms sidebar */}
            <Box sx={{ width: 240, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflowY: 'auto' }}>
              <Typography variant="overline" sx={{ px: 2, pt: 1.5, display: 'block' }} color="text.secondary">
                Rooms
              </Typography>
              {loadingLocData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress size={22} /></Box>
              ) : rooms.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  No rooms in this location
                </Typography>
              ) : (
                <List dense>
                  {rooms.map((r, i) => (
                    <ListItemButton
                      key={r.RoomID}
                      selected={String(r.RoomID) === String(roomId)}
                      onClick={() => setRoomId(r.RoomID)}
                      draggable={reordering}
                      onDragStart={reordering ? (e) => { setDragRoomIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } : undefined}
                      onDragOver={reordering ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                      onDrop={reordering ? (e) => { e.preventDefault(); onDropRoom(i); } : undefined}
                      sx={reordering ? { cursor: 'grab' } : undefined}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: 'text.primary' }}>
                        {reordering ? <DragIndicatorIcon fontSize="small" /> : <MeetingRoomIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={r.RoomName || `Room ${r.RoomID}`}
                        primaryTypographyProps={{ color: 'text.primary', noWrap: true }}
                      />
                      {reordering && (
                        <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" disabled={i === 0} onClick={() => moveRoom(i, -1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                          <IconButton size="small" disabled={i === rooms.length - 1} onClick={() => moveRoom(i, 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                        </Box>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>

            {/* Selected room's devices */}
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>
              {roomId == null ? (
                <EmptyHint icon={<MeetingRoomIcon />} text="Select a room to see its devices" />
              ) : loadingRoom ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Typography variant="h6" color="text.primary">
                      {selectedRoom?.RoomName || 'Room'}
                    </Typography>
                    {/* Room-wide firmware push — owner / super admin only. */}
                    {canUpdateFirmware && (
                      <Button
                        size="small"
                        color="warning"
                        startIcon={<SystemUpdateAltIcon fontSize="small" />}
                        onClick={() => setUpdateScope({
                          type: 'room', id: roomId, name: selectedRoom?.RoomName || 'Room',
                          locationId,
                        })}
                      >
                        Update Code
                      </Button>
                    )}
                  </Box>
                  {/* Per-room Scenes (max 8) — right-click to add; right-click a
                      scene to edit/delete; left-click to run. */}
                  <RoomScenesManager
                    key={roomId}
                    variant="inline"
                    roomId={roomId}
                    roomName={selectedRoom?.RoomName || 'Room'}
                    initialDevices={roomDevices}
                  />
                  {roomDevices.length === 0 ? (
                    <EmptyHint icon={<DevicesOtherIcon />} text="No devices in this room" />
                  ) : shownRoomDevices.length === 0 ? (
                    <EmptyHint icon={<DevicesOtherIcon />} text="Every device in this room is offline" />
                  ) : (
                    // The index is only ever used by the reorder handlers, and the
                    // filter is suspended while reordering — so it indexes the full
                    // list, exactly as persistDeviceOrder expects.
                    <Grid container spacing={1.25} alignItems="stretch">
                      {shownRoomDevices.map((d, i) => (
                        <Grid item {...deviceGridSpan(d.DeviceID)} key={unoOf(d)} sx={{ display: 'flex' }}>
                          <Box
                            sx={{ width: '100%', position: 'relative', cursor: reordering ? 'grab' : 'default' }}
                            draggable={reordering}
                            onDragStart={reordering ? (e) => { setDragDevIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } : undefined}
                            onDragOver={reordering ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                            onDrop={reordering ? (e) => { e.preventDefault(); onDropDev(i); } : undefined}
                          >
                            {reordering && (
                              <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                                <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={i === 0} onClick={() => moveDev(i, -1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                                <DragIndicatorIcon fontSize="small" sx={{ color: '#fff' }} />
                                <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={i === roomDevices.length - 1} onClick={() => moveDev(i, 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                              </Box>
                            )}
                            <Box sx={{ pointerEvents: reordering ? 'none' : 'auto' }}>
                              <DeviceControlCard device={d} />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </Box>
          </Box>
        </>
      )}

      <UpdateCodeDialog
        open={!!updateScope}
        scope={updateScope}
        onClose={() => setUpdateScope(null)}
      />
    </Box>
  );
}

function CommonStrip({ devices, loading }) {
  return (
    <Box sx={{ px: 2, py: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <GroupWorkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="overline" color="text.secondary">Common Devices</Typography>
        {!loading && <Chip size="small" label={devices.length} />}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>
      ) : devices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No common devices for this location</Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, alignItems: 'stretch' }}>
          {devices.map((d) => {
            // Width follows the PANEL COLUMNS, not the type — a 7+1 needs four
            // columns and a flat 230 px crushed them into each other.
            const w = deviceCardWidth(d.DeviceID);
            return (
              <Box key={unoOf(d)} sx={{ width: w, flexShrink: 0 }}>
                <DeviceControlCard device={d} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function EmptyHint({ icon, text }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 8, color: 'text.secondary' }}>
      <Box sx={{ fontSize: 48, display: 'flex' }}>{icon}</Box>
      <Typography color="text.secondary">{text}</Typography>
    </Box>
  );
}
